import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormGroup, ReactiveFormsModule } from "@angular/forms";
import { SelectModule } from "primeng/select";
import { CheckboxModule } from "primeng/checkbox";
import { ScheduledOrder } from "../../../../../core/models/scheduled-order.interface";
import { ScheduledOrderShippingRateOption } from "../../../../../core/models/scheduled-order-shipping-rate-option.interface";
import { ScheduledOrderService } from "../../../services/scheduled-order.service";
import { MessageService } from "primeng/api";
import { ScheduledOrderForm } from "../../../interfaces/scheduled-order-form.interface";
import { Subscription } from "rxjs";
import { QPilotAccessToken } from "../../../../../core/services/qpilot.service";

/**
 * Shipping rate selector component for choosing shipping rates for scheduled orders
 * Displays available shipping rate options and allows enabling auto shipping rate
 * COMPACT REDESIGN - Select dropdown with checkbox toggle for auto rates (matches old QMC)
 *
 * REFACTORED: Now uses reactive forms with FormGroup input per CLAUDE.md guidelines
 */
@Component({
  selector: "app-shipping-rate-selector",
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, SelectModule, CheckboxModule],
  templateUrl: "./shipping-rate-selector.component.html",
  styleUrl: "./shipping-rate-selector.component.scss",
})
export class ShippingRateSelectorComponent implements OnInit, OnDestroy {
  @Input() formGroup!: FormGroup<ScheduledOrderForm>;
  @Input() siteId!: number;
  @Input() disabled = false;
  @Input() required = false;
  @Input() accessToken?: QPilotAccessToken; // Optional token for embedded mode

  // Shipping rate options loaded from API (not in form - managed by component)
  private _shippingRateOptions: ScheduledOrderShippingRateOption[] = [];

  @Input() set shippingRateOptions(value: ScheduledOrderShippingRateOption[]) {
    this._shippingRateOptions = value || [];
    // Format options whenever they change
    this.formatShippingRateOptions();
  }

  get shippingRateOptions(): ScheduledOrderShippingRateOption[] {
    return this._shippingRateOptions;
  }

  // Emit when shipping rate options are loaded (parent needs to store these for save payload)
  @Output() shippingRateOptionsLoaded = new EventEmitter<
    ScheduledOrderShippingRateOption[]
  >();

  // Emit when auto shipping rate is toggled (parent needs to recalculate totals)
  @Output() autoRateToggled = new EventEmitter<boolean>();

  // Internal state for dropdown options (formatted for p-select)
  selectOptions: { label: string; value: string }[] = [];
  isLoading = false;
  canLoadRates = false;
  private initialized = false;

  // Subscriptions for cleanup
  private subscriptions: Subscription[] = [];

  // Input for order data needed for API calls (items, shipping address, etc.)
  private _orderData?: ScheduledOrder;

  @Input() set orderData(value: ScheduledOrder | undefined) {
    this._orderData = value;
    if (value) {
      this.checkIfCanLoadRates();
    }
  }

  get orderData(): ScheduledOrder | undefined {
    return this._orderData;
  }

  constructor(
    private scheduledOrderService: ScheduledOrderService,
    private messageService: MessageService,
  ) {}

  ngOnInit(): void {
    this.initialized = true;

    // Initialize EnableAutoShippingRate default if undefined (matches old QMC lines 373-375)
    const enableAutoControl = this.formGroup.controls.enableAutoShippingRate;
    if (
      enableAutoControl.value === undefined ||
      enableAutoControl.value === null
    ) {
      enableAutoControl.setValue(true, { emitEvent: false });
    }

    // Check if we can load rates
    this.checkIfCanLoadRates();

    // Format options if they were set before initialization (lazy-loaded accordion)
    if (this._shippingRateOptions?.length > 0) {
      this.formatShippingRateOptions();
    }

    // Subscribe to enableAutoShippingRate changes to clear preferred rate when auto is enabled
    const autoRateSub = enableAutoControl.valueChanges.subscribe((enabled) => {
      if (enabled) {
        // Clear preferred rate if auto is enabled (matches old QMC behavior)
        this.formGroup.controls.preferredShippingRateOption.setValue(null, {
          emitEvent: false,
        });
      }
    });
    this.subscriptions.push(autoRateSub);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  /**
   * Check if we have the minimum required data to load shipping rates
   * Requires: items and shipping address
   */
  checkIfCanLoadRates(): void {
    if (!this._orderData) {
      this.canLoadRates = false;
      return;
    }

    const hasItems = !!(
      this._orderData.ScheduledOrderItems &&
      this._orderData.ScheduledOrderItems.length > 0
    );
    const hasShippingAddress =
      !!this._orderData.ShippingCountry && !!this._orderData.ShippingPostcode;

    this.canLoadRates = !!(hasItems && hasShippingAddress);
  }

  /**
   * Load shipping rate options from API
   * CRITICAL: Calculate totals FIRST to sync ScheduledOrderItem prices with Product prices
   */
  loadShippingRates(): void {
    if (!this.siteId) {
      this.messageService.add({
        severity: "warn",
        summary: "Missing Site",
        detail: "Site ID is required to load shipping rates",
      });
      return;
    }

    if (!this.canLoadRates || !this._orderData) {
      this.messageService.add({
        severity: "warn",
        summary: "Incomplete Data",
        detail:
          "Please add items and shipping address before loading shipping rates",
      });
      return;
    }

    this.isLoading = true;

    // CRITICAL FIX: Calculate totals FIRST to sync item prices before fetching shipping rates
    // This ensures Subtotal is correct (e.g., if ScheduledOrderItem.SalePrice is stale)
    this.scheduledOrderService
      .calculateTotals(this.siteId, this._orderData, this.accessToken)
      .subscribe({
        next: (totals) => {
          // Update order data with calculated totals (for shipping rate calculation)
          if (this._orderData) {
            this._orderData.Subtotal = totals.SubTotal;
            this._orderData.TaxTotal = totals.TaxTotals?.Total || 0;
            this._orderData.Total = totals.Total;
          }

          // NOW fetch shipping rates with correct totals
          this.fetchShippingRates();
        },
        error: (error) => {
          console.error(
            "Error calculating totals before fetching shipping rates:",
            error,
          );
          // Continue anyway and try to fetch shipping rates
          this.fetchShippingRates();
        },
      });
  }

  /**
   * Fetch shipping rates from API (called after calculateTotals)
   */
  private fetchShippingRates(): void {
    if (!this._orderData) {
      this.isLoading = false;
      return;
    }

    this.scheduledOrderService
      .getShippingRateOptions(this.siteId, this._orderData, this.accessToken)
      .subscribe({
        next: (options) => {
          // Store options locally
          this.shippingRateOptions = options;
          this.formatShippingRateOptions();

          // CRITICAL: Emit the loaded options so parent can store them for save payload
          this.shippingRateOptionsLoaded.emit(options);

          if (options.length === 0) {
            this.messageService.add({
              severity: "info",
              summary: "No Rates Available",
              detail:
                "No shipping rates found. Auto shipping rate may be used instead.",
              life: 5000,
            });
          } else {
            // Auto-select default rate if not already selected and auto is disabled
            const defaultRate = options.find((opt) => opt.IsDefault);
            const currentPreferred =
              this.formGroup.controls.preferredShippingRateOption.value;
            const enableAuto =
              this.formGroup.controls.enableAutoShippingRate.value;

            if (defaultRate && !currentPreferred && !enableAuto) {
              this.formGroup.controls.preferredShippingRateOption.setValue(
                defaultRate.OptionValue,
              );
            }

            this.messageService.add({
              severity: "success",
              summary: "Rates Loaded",
              detail: `Found ${options.length} shipping rate option(s)`,
              life: 3000,
            });
          }
        },
        error: (error) => {
          console.error("Error loading shipping rates:", error);
          this.messageService.add({
            severity: "error",
            summary: "Load Failed",
            detail: "Failed to load shipping rates. Please try again.",
          });
        },
        complete: () => {
          this.isLoading = false;
        },
      });
  }

  /**
   * Format shipping rate options for select display
   */
  formatShippingRateOptions(): void {
    if (!this.shippingRateOptions) {
      this.selectOptions = [];
      return;
    }

    this.selectOptions = this.shippingRateOptions.map((option) => ({
      label: option.OptionText || option.OptionValue,
      value: option.OptionValue,
    }));
  }

  /**
   * Handle shipping rate selection change
   * @param event Select change event
   */
  onShippingRateChange(event: { value: string }): void {
    // Form control is already updated via [formControl] binding
    // No need to manually set - just log for debugging if needed
    console.log("[ShippingRateSelector] Rate changed to:", event.value);
  }

  /**
   * Handle auto shipping rate toggle change (checkbox)
   */
  onAutoShippingRateToggle(): void {
    // Form control is already updated via [formControl] binding
    // The valueChanges subscription in ngOnInit handles clearing preferred rate
    const isEnabled = this.formGroup.controls.enableAutoShippingRate.value;
    console.log("[ShippingRateSelector] Auto rate toggled:", isEnabled);

    // Emit event so parent can recalculate totals (matches old QMC behavior)
    this.autoRateToggled.emit(isEnabled ?? false);
  }

  /**
   * Check if a rate is the default rate
   * @param rateValue Rate option value
   * @returns True if the rate is default
   */
  isDefaultRate(rateValue: string): boolean {
    const rate = this.shippingRateOptions?.find(
      (opt) => opt.OptionValue === rateValue,
    );
    return rate?.IsDefault || false;
  }

  /**
   * Check if a shipping rate is selected
   * @returns True if a rate is selected
   */
  hasSelectedRate(): boolean {
    return !!this.formGroup.controls.preferredShippingRateOption.value;
  }

  /**
   * Get selected shipping rate details for display
   * @returns Shipping rate details object
   */
  getSelectedRateDetails(): ScheduledOrderShippingRateOption | undefined {
    const preferredOption =
      this.formGroup.controls.preferredShippingRateOption.value;
    if (!preferredOption) {
      return undefined;
    }

    return this.shippingRateOptions?.find(
      (opt) => opt.OptionValue === preferredOption,
    );
  }

  /**
   * Get human-readable status message
   * @returns Status message string
   */
  getStatusMessage(): string {
    if (this.formGroup.controls.enableAutoShippingRate.value) {
      return "Auto shipping rate is enabled. The shipping rate will be calculated automatically.";
    }

    if (this.hasSelectedRate()) {
      const details = this.getSelectedRateDetails();
      return `Selected: ${details?.OptionText || this.formGroup.controls.preferredShippingRateOption.value}`;
    }

    if (!this.canLoadRates) {
      return "Add items and shipping address to load shipping rates.";
    }

    return "No shipping rate selected. Please load and select a rate or enable auto shipping.";
  }

  /**
   * Get default shipping rate (matches old QMC lines 526-528)
   * @returns Default shipping rate option or undefined
   */
  getDefaultShippingRate(): ScheduledOrderShippingRateOption | undefined {
    return this.shippingRateOptions
      ? this.shippingRateOptions.find((s) => s.IsDefault)
      : undefined;
  }
}
